import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { formatNumber } from "@/lib/format";

const List = ({ title, items, tone, testid, render }) => (
  <div className={`rounded border p-3 ${tone}`} data-testid={testid}>
    <div className="mb-1 text-[12px] font-semibold uppercase tracking-wide">{title}</div>
    <div className="max-h-56 overflow-y-auto text-[12px] space-y-0.5">{items.map((x) => <div key={x.name} className="flex justify-between gap-2"><span className="truncate">{render(x)}</span></div>)}</div>
  </div>
);

function Preview({ pv }) {
  return (
    <div className="grid gap-3 lg:grid-cols-3">
      <List title={`Silinecek · ${formatNumber(pv.delete_total)} kayıt`} items={pv.delete} tone="border-rose-300 bg-rose-50 text-rose-900" testid="reset-delete-list"
        render={(x) => <><span className="font-mono">{x.name}</span> · {formatNumber(x.count)}</>} />
      <List title="Kısmen silinecek" items={pv.partial} tone="border-amber-300 bg-amber-50 text-amber-900" testid="reset-partial-list"
        render={(x) => <><span className="font-mono">{x.name}</span> · {x.label}: −{formatNumber(x.delete)} / kalan {formatNumber(x.keep)}</>} />
      <List title="Korunacak" items={pv.keep} tone="border-emerald-300 bg-emerald-50 text-emerald-900" testid="reset-keep-list"
        render={(x) => <><span className="font-mono">{x.name}</span> · {x.label} · {formatNumber(x.count)}</>} />
      <div className="lg:col-span-3 text-[12px] text-slate-600 space-y-0.5" data-testid="reset-notes">
        <div>Kod/GTIN kayıt defteri korunur; silinen ürünlere ait {formatNumber(pv.registry_to_retire)} kayıt "emekli / sistem sıfırlama" olarak işaretlenir — kullanılmış kodlar asla yeniden verilmez.</div>
        <div>Kasa/banka/POS hesap tanımları {pv.finance_accounts}. {pv.storage_note}</div>
        <div>Silmeden önce kalıcı ve kilitli <b>pre_reset_&lt;zaman&gt;</b> yedeği (yerel + S3) alınır ve doğrulanır; doğrulanamazsa hiçbir şey silinmez.</div>
      </div>
    </div>
  );
}

function Job({ job }) {
  const done = job.status === "tamamlandi";
  return (
    <div className={`rounded border p-3 text-[13px] ${done ? "border-emerald-300 bg-emerald-50" : job.error ? "border-rose-300 bg-rose-50" : "border-slate-300"}`} data-testid="reset-job">
      <div className="flex items-center gap-2 font-semibold">{!job.finished_at && <Loader2 className="h-4 w-4 animate-spin" />}Durum: <span data-testid="reset-job-status">{job.status}</span> · %{job.progress || 0}{job.current ? ` · ${job.current}` : ""}</div>
      {job.backup_name && <div>Yedek: <b data-testid="reset-job-backup">{job.backup_label}</b> ({job.backup_name}) · S3: {job.s3 || "—"}</div>}
      {done && <div>Silinen: <b>{formatNumber(job.total)}</b> kayıt · emekli kod/GTIN: {formatNumber(job.registry_retired)}</div>}
      {job.error && <div className="text-rose-800" data-testid="reset-job-error">{job.error}</div>}
      {(done || job.error) && <Link to="/ayarlar?tab=backup" className="text-blue-700 underline" data-testid="reset-backups-link">Yedekler (geri yükleme)</Link>}
    </div>
  );
}

export default function SystemResetTab() {
  const [pv, setPv] = useState(null);
  const [step, setStep] = useState(1);
  const [pw, setPw] = useState("");
  const [phrase, setPhrase] = useState("");
  const [tok, setTok] = useState(null);
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  useEffect(() => { api.get("/system/reset/preview").then(({ data }) => { setPv(data); if (data.last_job) setJob(data.last_job); }).catch((e) => toast.error(apiError(e))); return () => clearInterval(timer.current); }, []);
  const poll = (id) => { timer.current = setInterval(() => api.get(`/system/reset/jobs/${id}`).then(({ data }) => { setJob(data); if (data.finished_at) clearInterval(timer.current); }).catch(() => {}), 2000); };
  const execute = async (t, d = "") => {
    setBusy(true);
    try { const { data } = await api.post("/system/reset/execute", { token: t.token, domain: d }); setStep(4); poll(data.job_id); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/system/reset/confirm", { password: pw, phrase });
      setPw(""); setTok(data);
      if (data.needs_domain) setStep(3); else { setBusy(false); await execute(data); return; }
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  if (!pv) return <div className="panel h-40 animate-pulse" />;
  return (
    <div className="space-y-4" data-testid="system-reset-tab">
      <div className="rounded border-2 border-rose-500 bg-rose-50 p-4 text-rose-900" data-testid="reset-warning">
        <div className="flex items-center gap-2 text-base font-bold"><AlertTriangle className="h-5 w-5" />Sistemi Sıfırla — geri alınamaz (yalnız yedekten geri yüklenir)</div>
        <div className="text-[13px]">Tüm iş verisi (cariler, ürünler, stok, satış, alış, ithalat, çek/senet, kasa/banka, tahsilat, prim, inşaat…) ve Süper Yönetici dışındaki kullanıcılar silinir. Yedekler, ayarlar, roller, kod/GTIN kayıt defteri ve denetim kaydı korunur.{pv.is_prod && <b> CANLI ORTAM ({pv.domain})</b>}</div>
      </div>
      {job && <Job job={job} />}
      {step === 1 && <><Preview pv={pv} /><Button className="bg-rose-600 hover:bg-rose-700" onClick={() => setStep(2)} data-testid="reset-step1-next"><ShieldAlert className="h-4 w-4 mr-1" />Devam: Onayla</Button></>}
      {step === 2 && (
        <div className="panel p-4 space-y-3 max-w-md" data-testid="reset-step2">
          <label className="block text-[13px]">Süper Yönetici parolası<Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" data-testid="reset-password" /></label>
          <label className="block text-[13px]">Onay için tam olarak <b className="font-mono">{pv.phrase}</b> yazın<Input value={phrase} onChange={(e) => setPhrase(e.target.value)} data-testid="reset-phrase" /></label>
          <div className="flex gap-2"><Button variant="outline" onClick={() => setStep(1)}>Geri</Button>
            <Button disabled={busy || !pw || phrase !== pv.phrase} className="bg-rose-600 hover:bg-rose-700" onClick={confirm} data-testid="reset-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{pv.is_prod ? "Doğrula" : "Yedek Al ve Sıfırla"}</Button></div>
        </div>)}
      {step === 3 && tok && (
        <div className="panel p-4 space-y-3 max-w-md border-rose-400" data-testid="reset-step3">
          <div className="text-[13px]">Canlı ortam: <b>{tok.domain}</b>. Önce kalıcı <b>pre_reset_&lt;zaman&gt;</b> yedeği alınıp doğrulanacak. Onay belirteci 5 dakika geçerli.</div>
          <label className="block text-[13px]">Alan adını yazın ({tok.domain})<Input value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="reset-domain" /></label>
          <Button disabled={busy || domain.trim().toLowerCase() !== tok.domain} className="bg-rose-700 hover:bg-rose-800" onClick={() => execute(tok, domain)} data-testid="reset-execute">Yedek Al ve Canlıyı Sıfırla</Button>
        </div>)}
    </div>
  );
}
